import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { inmateService } from '../../../services/inmateService';

const FacilityDashboard = () => {
  const [populationData, setPopulationData] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('all');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [populationResult, facilitiesResult] = await Promise.all([
        inmateService?.getCurrentPopulation(),
        inmateService?.getFacilities()
      ]);

      if (populationResult?.success) {
        setPopulationData(populationResult?.data || []);
      } else {
        setError(populationResult?.error || 'Failed to load population data');
      }

      if (facilitiesResult?.success) {
        setFacilities(facilitiesResult?.data || []);
      } else {
        setError(facilitiesResult?.error || 'Failed to load facilities data');
      }
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getTotalPopulation = () => {
    return populationData?.reduce((sum, facility) => sum + (facility?.current_population || 0), 0);
  };

  const getFilteredData = () => {
    if (selectedFacility === 'all') {
      return populationData;
    }
    return populationData?.filter(facility => facility?.facility_id === selectedFacility) || [];
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="Loader" size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading facility dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 text-red-600 mb-4">
          <Icon name="AlertCircle" size={20} />
          <span className="font-medium">Error</span>
        </div>
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button
          onClick={loadDashboardData}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  const filteredData = getFilteredData();
  const totalPopulation = getTotalPopulation();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Facility Dashboard</h2>
          <p className="text-muted-foreground">
            Current inmate population by detention facility
          </p>
        </div>
        <button
          onClick={loadDashboardData}
          className="flex items-center space-x-2 bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-secondary/90 text-sm"
        >
          <Icon name="RefreshCw" size={16} />
          <span>Refresh</span>
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Population */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Users" size={24} color="white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalPopulation?.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total In Custody</p>
            </div>
          </div>
        </div>

        {/* Active Facilities */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
              <Icon name="Building" size={24} color="white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{facilities?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Active Facilities</p>
            </div>
          </div>
        </div>

        {/* Average per Facility */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
              <Icon name="BarChart" size={24} color="white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {facilities?.length > 0 ? Math.round(totalPopulation / facilities?.length) : 0}
              </p>
              <p className="text-sm text-muted-foreground">Average per Facility</p>
            </div>
          </div>
        </div>
      </div>
      {/* Filter */}
      <div className="flex items-center space-x-4">
        <label className="text-sm font-medium text-foreground">Filter by Facility:</label>
        <select
          value={selectedFacility}
          onChange={(e) => setSelectedFacility(e?.target?.value)}
          className="px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">All Facilities</option>
          {facilities?.map(facility => (
            <option key={facility?.facility_id} value={facility?.facility_id}>
              {facility?.facility_name}
            </option>
          ))}
        </select>
      </div>
      {/* Population Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Current Population by Facility</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Facility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Facility ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Current Population
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  % of Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredData?.map((facility) => (
                <tr key={facility?.facility_id} className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Building" size={16} className="text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {facility?.facility_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {facility?.facility_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-foreground">
                      {facility?.current_population?.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{
                            width: `${totalPopulation > 0 
                              ? (facility?.current_population / totalPopulation) * 100 
                              : 0}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {totalPopulation > 0 
                          ? ((facility?.current_population / totalPopulation) * 100)?.toFixed(1)
                          : 0}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                      facility?.current_population > 0 
                        ? 'bg-accent/10 text-accent' :'bg-muted text-muted-foreground'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        facility?.current_population > 0 ? 'bg-accent' : 'bg-muted-foreground'
                      }`}></div>
                      <span>{facility?.current_population > 0 ? 'Active' : 'Empty'}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredData?.length === 0 && (
          <div className="py-12 text-center">
            <Icon name="Building" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No facility data available</p>
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors text-left">
            <Icon name="Search" size={20} className="text-primary mb-2" />
            <p className="text-sm font-medium text-foreground">Search Inmates</p>
            <p className="text-xs text-muted-foreground">Find inmates by facility</p>
          </button>
          
          <button className="p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors text-left">
            <Icon name="Download" size={20} className="text-accent mb-2" />
            <p className="text-sm font-medium text-foreground">Export Report</p>
            <p className="text-xs text-muted-foreground">Generate facility report</p>
          </button>
          
          <button className="p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors text-left">
            <Icon name="Settings" size={20} className="text-secondary mb-2" />
            <p className="text-sm font-medium text-foreground">Manage Facilities</p>
            <p className="text-xs text-muted-foreground">Add/edit facilities</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacilityDashboard;